/**
 * Common-strategies content for the Instructions panel.
 * Pure data so it's easy to translate, version, or swap later.
 */

export interface Strategy {
  title: string;
  body: string;
  difficulty: "starter" | "intermediate" | "advanced";
}

export const STRATEGIES: Strategy[] = [
  {
    title: "Match value to threaten Encounter",
    difficulty: "starter",
    body: "Your fastest route to a capture is to march a piece toward an enemy piece of the same value. White (Evens) and Black (Odds) carry different value sets, but several values overlap (e.g. White Triangle-9 and Black Round-9 can capture each other). The King's full pyramidal number is unique per side, but individual King layers (e.g. value 16 on both sides) are matchable.",
  },
  {
    title: "Combine two pieces to threaten Assault",
    difficulty: "starter",
    body: "Two of your pieces that can both reach a single enemy square may capture by sum, difference, product, or integer-exact division. Round-3 + Round-4 threatens any enemy of value 7 or 12. Look for arithmetic relationships in your pawn structure: pieces with values that sum (or divide!) to common targets are far more dangerous together than apart.",
  },
  {
    title: "Triangles raid; protect them with Squares",
    difficulty: "starter",
    body: "Triangles cover 2 squares per orthogonal move (path must be clear) and have a flying knight's-hop (1,2) that leaps but can't capture. They're some of your fastest raiders. But Triangles are also the easiest pieces to Assault: their values are natural sums, products, and quotients of enemy Rounds. Keep your Triangles within rescue range of a Square or your King.",
  },
  {
    title: "Squares are heavy artillery — but blockable",
    difficulty: "intermediate",
    body: "A Square moves 3 orthogonal squares without jumping (path must be clear), plus a flying extended-knight (1,3) that leaps but can't capture. Its high value (up to 361 on the Black side) makes it a tempting prize, and any single piece in its standard path stops it cold. Use Squares as area-denial pieces, not raiders. Their best Encounter targets are enemy Squares of matching value sitting on the same rank or file with a clear lane.",
  },
  {
    title: "King (Pyramid): mode flexibility — but watch the layers",
    difficulty: "intermediate",
    body: "Each turn you declare whether the King moves like a Round, Triangle, or Square. That makes it the only piece your opponent cannot fully predict. But the King is layered: once every layer of a given shape is captured, the King loses that movement mode. White's King has 2 Round / 2 Triangle / 2 Square layers; Black's has 1 / 2 / 2. Layer-by-layer attrition matters — and you cannot win until the enemy King is gone, whole or stripped to nothing.",
  },
  {
    title: "Set Sieges in the corners first",
    difficulty: "intermediate",
    body: "A corner needs only 2 of your pieces (the board edges supply the other two walls), an edge needs 3, the open board needs 4. Sieges trigger automatically when the last side is blocked, so they're never declined. Force enemy pieces toward A1, A16, H1, or H16 by occupying the squares they want to retreat to.",
  },
  {
    title: "Aim for {3, 4, 6, 9} in enemy territory",
    difficulty: "advanced",
    body: "These four Round values together satisfy all three progressions (AP {3,6,9}, GP {4,6,9}, HP {3,4,6}) — the Glorious Victory set. If you're playing Glorious or Royal, your strategic goal isn't capture; it's getting these four Rounds past the midline simultaneously. Trade material aggressively to clear lanes for them.",
  },
  {
    title: "Watch for {4, 6, 9, 12} as a second Glorious set",
    difficulty: "advanced",
    body: "Less famous than {3,4,6,9} but equally complete: AP {6,9,12}, GP {4,6,9}, HP {4,6,12}. The advantage is that these values span two shapes (4 is a Square, 6 a Round, 9 a Round, 12 a Triangle), giving you more flexibility in which pieces to advance. The disadvantage is the Triangle-12 is a common Encounter target.",
  },
  {
    title: "Defend by breaking arithmetic relationships",
    difficulty: "advanced",
    body: "If your opponent has Round-3 and Round-4 in range of your Round-7, an Assault is one move away. Don't move the Round-7; move one of THEIR support pieces' relationships out of phase. Often a free Round of yours can step between the two attackers and break the chain. Defense in Rithmomachia is positional, not reactive.",
  },
  {
    title: "Don't hoard captures past the threshold",
    difficulty: "intermediate",
    body: "In Common or Proper Victory, the moment your tray crosses the threshold the game ends — you don't need to keep capturing. Pivot to defense as soon as you see the line of victory. In progression-based victory levels, captures matter only insofar as they remove pieces blocking your formation in the triumph zone (and, for Greater/Royal/Glorious, knock the enemy King off the board). Sometimes the best capture is the one you decline.",
  },
];
